import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import mixpanel from "mixpanel-browser";
import ContentContainer from "../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Button, { ButtonSizes, ButtonThemes } from "../../../ui-components/button/button";
import { DataTable } from "../../../ui-components/data-table";
import { PAGE_IDS } from "../../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../../app/data/common/queryKeys";
import { Routes } from "../../../app/route/RoutesConfig";
import { userSelector } from "../../../slices/user/userSlice";
import CommunicationsService from "../../../app/data/communications/communicationsService";
import PickupRequestsService from "../../../app/data/pickup-requests/pickupRequestsService";
import {
  InboundMessageListItem,
  PickupReviewStatus,
} from "../../../app/data/communications/models";
import { ReactComponent as ChartIcon } from "../../../images/chart.svg";
import EmailViewerSidebar from "../../common/email-viewer/EmailViewerSidebar";
import PickupDetails from "../../common/pickup-details/pickupDetails";
import { UpdatePickupModel } from "../../../app/data/pickup/models";
import { pickupDetailsSelector } from "../../../slices/pickup/pickupDetailsSlice";
import { getPickup } from "../../../slices/pickup/pickupDetailsSlice";
import {
  getDriverById,
  pickupAssignmentSelector,
} from "../../../slices/pickup/pickupAssignmentSlice";
import { getPickupDetails } from "../../../services/pickups";
import getPickupInboxColumns, {
  PICKUP_INBOX_COLUMN_OPTIONS,
  PICKUP_INBOX_COLUMNS_HIDDEN_BY_DEFAULT,
} from "./inboxColumns";
import { PickupInboxPath, PickupInboxPathParams } from "./route";
import InboxSummaryTiles, { InboxSummaryFilter } from "./components/inboxSummaryTiles";
import InboxFilters, { InboxFiltersState } from "./components/inboxFilters";
import MessageDetailSidebar from "./components/messageDetailSidebar";
import { INBOX_TRANSACTIONAL_QUERY_OPTIONS } from "./utils/pickupInboxQueryOptions";
import "./pickupInbox.scss";
import { useDataTableColumnPreferences } from "../../../hooks/useDataTableColumnPreferences";

const communicationsService = CommunicationsService.getInstance();
const pickupRequestsService = PickupRequestsService.getInstance();

const PAGE_SIZE = 25;
const DEBOUNCE_MS = 500;

interface InboxUrlFilters {
  emailText: string;
  sender: string;
  bolNumber: string;
  poNumber: string;
  oneXgsPickupNumber: string;
  reviewStatus: PickupReviewStatus | "";
  hasPendingReview: boolean;
}

const readFiltersFromSearch = (search: string): InboxUrlFilters => {
  const q = new URLSearchParams(search);
  return {
    emailText: q.get("emailText") || "",
    sender: q.get("sender") || "",
    bolNumber: q.get("bolNumber") || "",
    poNumber: q.get("poNumber") || "",
    oneXgsPickupNumber: q.get("oneXgsPickupNumber") || "",
    reviewStatus: (q.get("reviewStatus") as PickupReviewStatus) || "",
    hasPendingReview: q.get("hasPendingReview") === "true",
  };
};

const getInitialInboxFilters = (search: string): InboxUrlFilters =>
  readFiltersFromSearch(search);

const PickupInboxPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const params = useParams() as PickupInboxPathParams;
  const queryClient = useQueryClient();
  const userState = useSelector(userSelector);
  const pickupDetailsState = useSelector(pickupDetailsSelector);
  const pickupAssignmentState = useSelector(pickupAssignmentSelector);

  const messageIdParam = params[PickupInboxPath.messageId];
  const selectedMessageId = messageIdParam ? Number(messageIdParam) : null;

  const [emailViewerMessage, setEmailViewerMessage] = useState<InboundMessageListItem | null>(null);
  const [pickupDetailsNumber, setPickupDetailsNumber] = useState("");
  const [pickupDetailsDriverName, setPickupDetailsDriverName] = useState("");
  const [summaryFilter, setSummaryFilter] = useState<InboxSummaryFilter>(null);

  const [emailTextInput, setEmailTextInput] = useState(
    () => getInitialInboxFilters(location.search).emailText
  );
  const [debouncedEmailText, setDebouncedEmailText] = useState(
    () => getInitialInboxFilters(location.search).emailText
  );
  const [senderInput, setSenderInput] = useState(
    () => getInitialInboxFilters(location.search).sender
  );
  const [debouncedSender, setDebouncedSender] = useState(
    () => getInitialInboxFilters(location.search).sender
  );
  const [bolNumberInput, setBolNumberInput] = useState(
    () => getInitialInboxFilters(location.search).bolNumber
  );
  const [debouncedBolNumber, setDebouncedBolNumber] = useState(
    () => getInitialInboxFilters(location.search).bolNumber
  );
  const [poNumberInput, setPoNumberInput] = useState(
    () => getInitialInboxFilters(location.search).poNumber
  );
  const [debouncedPoNumber, setDebouncedPoNumber] = useState(
    () => getInitialInboxFilters(location.search).poNumber
  );
  const [pickupNumberInput, setPickupNumberInput] = useState(
    () => getInitialInboxFilters(location.search).oneXgsPickupNumber
  );
  const [debouncedPickupNumber, setDebouncedPickupNumber] = useState(
    () => getInitialInboxFilters(location.search).oneXgsPickupNumber
  );
  const [reviewStatusFilter, setReviewStatusFilter] = useState<PickupReviewStatus | "">(
    () => getInitialInboxFilters(location.search).reviewStatus
  );
  const [hasPendingReviewFilter, setHasPendingReviewFilter] = useState(
    () => getInitialInboxFilters(location.search).hasPendingReview
  );

  const columnPrefs = useDataTableColumnPreferences({
    instanceKey: "pickup-inbox",
    columnOptions: PICKUP_INBOX_COLUMN_OPTIONS,
    hiddenByDefault: PICKUP_INBOX_COLUMNS_HIDDEN_BY_DEFAULT,
  });

  const expandPickupRequestId = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const id = q.get("pickupRequestId");
    return id ? Number(id) : null;
  }, [location.search]);

  const terminalPrefix = userState.activeTerminal?.id
    ? `/${userState.activeTerminal.id}`
    : "";

  const hasActiveFilters =
    !!debouncedEmailText ||
    !!debouncedSender ||
    !!debouncedBolNumber ||
    !!debouncedPoNumber ||
    !!debouncedPickupNumber ||
    !!reviewStatusFilter ||
    hasPendingReviewFilter ||
    summaryFilter != null;

  const syncUrl = useCallback(
    (messageId: number | null, pickupRequestId?: number | null) => {
      const q = new URLSearchParams();
      if (hasPendingReviewFilter) q.set("hasPendingReview", "true");
      if (reviewStatusFilter) q.set("reviewStatus", reviewStatusFilter);
      if (debouncedEmailText) q.set("emailText", debouncedEmailText);
      if (debouncedSender) q.set("sender", debouncedSender);
      if (debouncedBolNumber) q.set("bolNumber", debouncedBolNumber);
      if (debouncedPoNumber) q.set("poNumber", debouncedPoNumber);
      if (debouncedPickupNumber) q.set("oneXgsPickupNumber", debouncedPickupNumber);
      if (pickupRequestId) q.set("pickupRequestId", String(pickupRequestId));

      const path = messageId
        ? `${terminalPrefix}${Routes.pickup.inbox}/${messageId}`
        : `${terminalPrefix}${Routes.pickup.inbox}`;
      const search = q.toString();
      history.replace({ pathname: path, search: search ? `?${search}` : "" });
    },
    [
      terminalPrefix,
      hasPendingReviewFilter,
      reviewStatusFilter,
      debouncedEmailText,
      debouncedSender,
      debouncedBolNumber,
      debouncedPoNumber,
      debouncedPickupNumber,
      history,
    ]
  );

  const makeDebouncedSetter = useCallback(
    (setter: (v: string) => void) => debounce(setter, DEBOUNCE_MS),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetEmailText = useCallback(makeDebouncedSetter(setDebouncedEmailText), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSender = useCallback(makeDebouncedSetter(setDebouncedSender), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetBolNumber = useCallback(makeDebouncedSetter(setDebouncedBolNumber), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetPoNumber = useCallback(makeDebouncedSetter(setDebouncedPoNumber), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetPickupNumber = useCallback(makeDebouncedSetter(setDebouncedPickupNumber), []);

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "pickup_inbox" });
  }, []);

  const isInitialMount = React.useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    syncUrl(selectedMessageId, expandPickupRequestId);
  }, [
    debouncedEmailText,
    debouncedSender,
    debouncedBolNumber,
    debouncedPoNumber,
    debouncedPickupNumber,
    reviewStatusFilter,
    hasPendingReviewFilter,
    selectedMessageId,
    expandPickupRequestId,
    syncUrl,
  ]);

  const { data: statsSummary, isLoading: statsLoading } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_STATS_SUMMARY, "7d"],
    queryFn: () => pickupRequestsService.getStatsSummary({ preset: "7d" }),
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const listQueryKey = [
    QUERY_KEYS.PICKUP_INBOX_MESSAGES,
    debouncedEmailText,
    debouncedSender,
    debouncedBolNumber,
    debouncedPoNumber,
    debouncedPickupNumber,
    reviewStatusFilter,
    hasPendingReviewFilter,
  ];

  const {
    data: messagesData,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isPreviousData,
  } = useInfiniteQuery({
    queryKey: listQueryKey,
    queryFn: async ({ pageParam }) =>
      communicationsService.getInboundMessages({
        pageSize: PAGE_SIZE,
        next: pageParam as string | undefined,
        emailText: debouncedEmailText || undefined,
        sender: debouncedSender || undefined,
        bolNumber: debouncedBolNumber || undefined,
        poNumber: debouncedPoNumber || undefined,
        oneXgsPickupNumber: debouncedPickupNumber || undefined,
        reviewStatus: reviewStatusFilter || undefined,
        hasPendingReview: hasPendingReviewFilter || undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.pagination?.next ?? undefined,
    keepPreviousData: true,
    retry: false,
  });

  const messages = useMemo(
    () => messagesData?.pages.flatMap((p) => p.messages) ?? [],
    [messagesData]
  );

  const hasNextPage = !!messagesData?.pages[messagesData.pages.length - 1]?.pagination?.next;

  const onViewEmail = useCallback((row: InboundMessageListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setEmailViewerMessage(row);
  }, []);

  const columns = useMemo(
    () => getPickupInboxColumns({ onViewEmail }),
    [onViewEmail]
  );

  const onRowClick = (row: InboundMessageListItem) => {
    syncUrl(row.id, expandPickupRequestId);
  };

  const onCloseDetail = () => {
    syncUrl(null);
  };

  const onCloseEmailViewer = () => setEmailViewerMessage(null);

  const onPickupNumberClick = (pickupNumber: string) => {
    setPickupDetailsNumber(pickupNumber);
    dispatch(getPickup(pickupNumber));
  };

  const onClosePickupDetails = () => {
    setPickupDetailsNumber("");
  };

  useEffect(() => {
    setPickupDetailsDriverName("");
    const driverId = pickupDetailsState.pickup?.pickup?.driverId;
    if (driverId == null) return;
    dispatch(getDriverById(driverId, (name) => setPickupDetailsDriverName(name), true));
  }, [dispatch, pickupDetailsState.pickup?.pickup?.pickupId, pickupDetailsState.pickup?.pickup?.driverId]);

  const invalidateInboxQueries = useCallback(() => {
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_MESSAGES]);
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_STATS_SUMMARY]);
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_STATS_ANALYTICS]);
    if (selectedMessageId) {
      queryClient.invalidateQueries([
        QUERY_KEYS.PICKUP_INBOX_MESSAGE_DETAIL,
        selectedMessageId,
      ]);
    }
  }, [queryClient, selectedMessageId]);

  const onUpdatePickupFromDetails = useCallback(
    (_pickup: UpdatePickupModel) => {
      invalidateInboxQueries();
      if (pickupDetailsNumber) {
        dispatch(getPickup(pickupDetailsNumber));
      }
    },
    [dispatch, invalidateInboxQueries, pickupDetailsNumber]
  );

  const onReschedulePickupFromDetails = useCallback(
    (_pickup: UpdatePickupModel) => {
      invalidateInboxQueries();
      if (pickupDetailsNumber) {
        dispatch(getPickup(pickupDetailsNumber));
      }
    },
    [dispatch, invalidateInboxQueries, pickupDetailsNumber]
  );

  const pickupDetails = useMemo(() => {
    if (!pickupDetailsState.pickup) return null;
    const base = getPickupDetails(pickupDetailsState.pickup);
    if (!base) return null;
    const driverName = pickupDetailsDriverName || base.driverName;
    return driverName ? { ...base, driverName } : base;
  }, [pickupDetailsState.pickup, pickupDetailsDriverName]);

  const onSummaryApply = (filter: InboxSummaryFilter) => {
    setSummaryFilter(filter);
    if (filter?.type === "hasPendingReview") {
      setHasPendingReviewFilter(true);
      setReviewStatusFilter("");
    } else if (filter?.type === "reviewStatus") {
      setReviewStatusFilter(filter.value);
      setHasPendingReviewFilter(false);
    } else if (filter?.type === "clearOutcomes") {
      setReviewStatusFilter("");
      setHasPendingReviewFilter(false);
    }
  };

  const onClearFilters = () => {
    setEmailTextInput("");
    setDebouncedEmailText("");
    setSenderInput("");
    setDebouncedSender("");
    setBolNumberInput("");
    setDebouncedBolNumber("");
    setPoNumberInput("");
    setDebouncedPoNumber("");
    setPickupNumberInput("");
    setDebouncedPickupNumber("");
    setReviewStatusFilter("");
    setHasPendingReviewFilter(false);
    setSummaryFilter(null);
  };

  const onReviewComplete = () => {
    invalidateInboxQueries();
  };

  const filtersState: InboxFiltersState = {
    emailText: emailTextInput,
    sender: senderInput,
    bolNumber: bolNumberInput,
    poNumber: poNumberInput,
    oneXgsPickupNumber: pickupNumberInput,
    reviewStatus: reviewStatusFilter,
    hasPendingReview: hasPendingReviewFilter,
  };

  const selectedListRow = messages.find((m) => m.id === selectedMessageId);

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ "full-width": true }}
      pageId={PAGE_IDS.PICKUP_INBOX}
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.PICKUP_INBOX}
          title="Inbox"
          mods={{ "full-width": true }}
        >
          <Button
            theme={ButtonThemes.transparent}
            size={ButtonSizes.auto}
            onClick={() =>
              history.push(`${terminalPrefix}${Routes.pickup.inboxAnalytics}${location.search}`)
            }
          >
            <div className="xgs-btn__icon-container">
              <ChartIcon className="xgs-btn__icon" />
            </div>
            Analytics
          </Button>
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-pickup-inbox">
        <InboxSummaryTiles
          stats={statsSummary}
          isLoading={statsLoading}
          filtersApplied={hasActiveFilters}
          onApplyFilter={onSummaryApply}
        />

        <InboxFilters
          filters={filtersState}
          onEmailTextChange={(v) => {
            setEmailTextInput(v);
            debouncedSetEmailText(v);
            setSummaryFilter(null);
          }}
          onSenderChange={(v) => {
            setSenderInput(v);
            debouncedSetSender(v);
            setSummaryFilter(null);
          }}
          onBolNumberChange={(v) => {
            setBolNumberInput(v);
            debouncedSetBolNumber(v);
            setSummaryFilter(null);
          }}
          onPoNumberChange={(v) => {
            setPoNumberInput(v);
            debouncedSetPoNumber(v);
            setSummaryFilter(null);
          }}
          onOneXgsPickupNumberChange={(v) => {
            setPickupNumberInput(v);
            debouncedSetPickupNumber(v);
            setSummaryFilter(null);
          }}
          onReviewStatusChange={(v) => {
            setReviewStatusFilter(v);
            setSummaryFilter(null);
          }}
          onClearFilters={onClearFilters}
        />

        <DataTable
          columns={columns}
          data={messages}
          isLoading={isLoading || (isFetching && isPreviousData)}
          keepTableOnLoading
          cursorPointer
          ignoreCellClick="viewEmail"
          rowHeight={48}
          noResultsText="No emails found. Adjust search or filters."
          infiniteScroll
          infiniteScrollLoading={isFetchingNextPage}
          infiniteScrollHasNext={hasNextPage}
          onInfiniteScroll={() => fetchNextPage()}
          onRowClicked={onRowClick}
          showColumnSelector
          columnSelectorOptions={PICKUP_INBOX_COLUMN_OPTIONS}
          {...columnPrefs}
          enableColumnResize
          enableColumnReorder
          stickyHeaders
          responsive
        />
      </div>

      <MessageDetailSidebar
        messageId={selectedMessageId}
        show={selectedMessageId != null}
        onClose={onCloseDetail}
        expandPickupRequestId={expandPickupRequestId}
        onViewEmail={() => {
          if (selectedListRow) setEmailViewerMessage(selectedListRow);
        }}
        onPickupNumberClick={onPickupNumberClick}
        onReviewComplete={onReviewComplete}
      />

      <EmailViewerSidebar
        messageId={emailViewerMessage?.id ?? null}
        show={!!emailViewerMessage}
        onClose={onCloseEmailViewer}
        metadata={
          emailViewerMessage
            ? {
                sender: emailViewerMessage.sender,
                receiver: emailViewerMessage.receiver,
                subject: emailViewerMessage.subject,
                receivedAt: emailViewerMessage.receivedAt,
              }
            : undefined
        }
        webLink={emailViewerMessage?.webLink}
      />

      <PickupDetails
        isOpen={!!pickupDetailsNumber}
        pickup={pickupDetails}
        onClose={onClosePickupDetails}
        onUpdatePickup={onUpdatePickupFromDetails}
        onReschedulePickup={onReschedulePickupFromDetails}
        isDriverLoading={
          pickupAssignmentState.requestStarted &&
          pickupAssignmentState.requestCreator === "GET_DRIVER"
        }
        isLoading={
          pickupDetailsState.requestStarted &&
          pickupDetailsState.requestCreator === "GET_PICKUP"
        }
        error={
          pickupDetailsState.requestCreator === "GET_PICKUP"
            ? pickupDetailsState.requestError
            : ""
        }
      />
    </ContentContainer>
  );
};

export default PickupInboxPage;
